<template>
    <div class="amEffect">
        <span v-if="tweenVarNew.prop" class="amEffect__label-start">
            {{ startLabel }}
        </span>

        <div class="amEffect__dropdown">
            <select class="amEffect__dropdown-select" :value="tweenVarNew.prop" @input="onInputProp">
                <option value="" disabled>Select One</option>

                <optgroup v-for="(optionGroup, i) in optionGroups" :key="i" :label="optionGroup.label">
                    <option v-for="(option, j) in optionGroup.options" :key="j" :value="option.value">
                        {{ option.text }}
                    </option>
                </optgroup>
            </select>

            <font-awesome-icon class="amEffect__dropdown-arrow" :icon="['far', 'chevron-down']" />
        </div>

        <span v-if="tweenVarNew.prop" class="amEffect__label-middle">
            {{ middleLabel }}
        </span>

        <component
            :is="tweenVars.get(tweenVarNew.prop).component"
            v-if="tweenVarNew.prop"
            class="amEffect__value"
            :value="tweenVarNew.value"
            :prop="tweenVarNew.prop"
            @input="onInputValue"
        />

        <div class="amEffect__label-end">
            <span v-if="unitOptions.length === 1">
                {{ unitOptions[0].text }}
            </span>

            <div v-if="unitOptions.length > 1" class="amEffect__dropdown">
                <select
                    class="amEffect__dropdown-select"
                    :value="tweenVarNew.unit"
                    @input="onInputUnit"
                >
                    <option v-for="unitOption in unitOptions" :key="unitOption.value" :value="unitOption.value">
                        {{ unitOption.text }}
                    </option>
                </select>

                <font-awesome-icon class="amEffect__dropdown-arrow" :icon="['far', 'chevron-down']" />
            </div>
        </div>

        <button type="button" class="amEffect__delete" @click="onDelete">
            <font-awesome-icon :icon="['fal', 'times']" />
        </button>
    </div>
</template>

<script>
import TweenInputColor from '@/components/settings/component/animations/tween-input-color';
import TweenInputNumber from '@/components/settings/component/animations/tween-input-number';
import TweenInputText from '@/components/settings/component/animations/tween-input-text';
import tweenVars, { tweenVarProps, tweenVarCategories } from '@/data/animations/tween-vars';
import mapFilters from '@/utils/map-filters';

export default {
    name: 'animation-effect',

    components: {
        TweenInputColor,
        TweenInputNumber,
        TweenInputText,
    },

    props: {
        // this helps prevents users from setting the same effect multiple times
        selectedProps: {
            type: Array,
            default: () => [],
        },

        tweenVar: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            tweenVarNew: {},
            tweenVars,
        };
    },

    computed: {
        optionGroups() {
            return Object.values(tweenVarCategories)
                .map((category) => {
                    const options = [];

                    for (const [, tweenVar] of tweenVars) {
                        const conflicts = {
                            [tweenVarProps.UP]: tweenVarProps.DOWN,
                            [tweenVarProps.DOWN]: tweenVarProps.UP,
                            [tweenVarProps.LEFT]: tweenVarProps.RIGHT,
                            [tweenVarProps.RIGHT]: tweenVarProps.LEFT,
                        };

                        const alreadyUsed = this.selectedProps.includes(tweenVar.prop) && tweenVar.prop !== this.tweenVarNew.prop;
                        const hasConflict = this.selectedProps.includes(conflicts[tweenVar.prop]);
                        const isAllowed = !alreadyUsed && !hasConflict;

                        if (tweenVar.category === category && isAllowed) {
                            options.push({
                                text: tweenVar.label,
                                value: tweenVar.prop,
                            });
                        }
                    }

                    return {
                        label: this.titleCase(category),
                        options,
                    };
                })
                .filter(category => !!category.options.length);
        },

        unitOptions() {
            if (!this.tweenVarNew.prop) return [];

            const tweenVarDefinition = tweenVars.get(this.tweenVarNew.prop);

            return tweenVarDefinition.units.map((unit) => {
                return { text: unit, value: unit };
            });
        },

        startLabel() {
            switch (this.tweenVarNew.prop) {
                case tweenVarProps.UP:
                case tweenVarProps.DOWN:
                case tweenVarProps.LEFT:
                case tweenVarProps.RIGHT:
                    return 'Move';

                default:
                    return 'Set';
            }
        },

        middleLabel() {
            switch (this.tweenVarNew.prop) {
                case tweenVarProps.UP:
                case tweenVarProps.DOWN:
                case tweenVarProps.LEFT:
                case tweenVarProps.RIGHT:
                    return 'by';

                default:
                    return 'to';
            }
        },
    },

    created() {
        this.tweenVarNew = this.tweenVar;
    },

    methods: {
        ...mapFilters(['titleCase']),

        onInputProp(e) {
            const prop = e.target.value;

            const { category, component, label, units } = tweenVars.get(prop);

            this.tweenVarNew.category = category;
            this.tweenVarNew.component = component;
            this.tweenVarNew.label = label;
            this.tweenVarNew.prop = prop;
            this.tweenVarNew.unit = units[0]; // default to the first listed unit
            this.tweenVarNew.value = '';

            this.onInput();
        },

        onInputValue(value) {
            // certain values need to be mutated before being passed on to GSAP
            value = this.mutateValue(value);

            this.tweenVarNew.value = value;
            this.onInput();
        },

        onInputUnit(e) {
            this.tweenVarNew.unit = e.target.value;
            this.onInput();
        },

        onInput() {
            this.$emit('input', this.tweenVarNew);
        },

        onDelete() {
            this.$emit('delete', this.tweenVarNew.id);
        },

        mutateValue(value) {
            switch (true) {
                // GSAP uses only two props for controlling direction - "x" and "y"
                // so to go "up" or "left", we need to add a negative sign
                case this.tweenVarNew.prop === tweenVarProps.UP:
                case this.tweenVarNew.prop === tweenVarProps.LEFT:
                    return value = `-${ value }`;

                // opacity is entered by the user as a percentage (e.g. "50")
                // but GSAP needs a value between 0 and 1 (e.g. "0.5")
                case this.tweenVarNew.prop === tweenVarProps.OPACITY:
                    return value / 100;

                default:
                    return value;
            }
        },
    },
};
</script>

<style lang="scss">
.amEffect {
    $label-spacing: 1em;
    display: flex;
    align-items: center;
    width: 100%;
    font-size: 0.875em;

    .color-picker__content {
        width: 100%;
    }

    &__label-start,
    &__label-middle,
    &__label-end {
        color: var(--form-labelColor);
    }

    &__label-start {
        margin-right: $label-spacing;
        width: 2.25em;
        text-align: right;
    }

    &__label-middle {
        margin: 0 $label-spacing 0 1em;
    }

    &__label-end {
        margin-left: $label-spacing;
    }

    &__dropdown {
        position: relative;
    }

    &__dropdown-select {
        -webkit-appearance: none;
        -webkit-border-radius: 0;
        border-radius: 0;
        padding: 0.125em 2em 0.125em 0.5em;
        background-color: var(--app-backgroundColor);
        border: 1px solid var(--app-borderColor);
        font-weight: 600;

        &:hover {
            cursor: pointer;
        }
    }

    &__dropdown-arrow {
        position: absolute;
        right: 0.5em;
        top: 50%;
        font-size: 0.875em;
        transform: translateY(-50%);
    }

    &__value {
        display: flex !important;
    }

    &__delete {
        position: absolute;
        right: 0;
        top: 0;
        bottom: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0 0.85em;
        font-size: 1.25rem;
        color: var(--app-linkColor);

        &:hover {
            color: var(--app-linkHoverColor);
        }
    }
}
</style>
